import { types as t } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import { observable, flow, makeObservable, IObservableArray } from 'mobx'
import fetch from '../../utils/fetch'
import Mapping, { IMappingSnapshot } from './mapping'
import Field from './field'
import DataTransformer from './filter'

export class DynamoDBProvider {
  data = [] as IObservableArray
  isLoading = true
  dataFetched = false
  LastEvaluatedKey = null
  config

  constructor (config) {
    makeObservable(this, {
      data: observable.shallow,
      isLoading: observable,
      dataFetched: observable,
      LastEvaluatedKey: observable
    })

    this.config = config
  }

  _query = async (ExclusiveStartKey = undefined) => {
    let params
    if (this.config.params) {
      params = await this.config.params.mapper.convertProps()
    }

    const res = await fetch(`/api/datasource/${this.config.dbKey}/query`, {
      method: 'POST',
      body: JSON.stringify({ ...(params || {}), ExclusiveStartKey }),
      headers: { 'Content-Type': 'application/json' }
    })
    return res.json()
  }

  query = flow(function * (this: DynamoDBProvider) {
    this.isLoading = true
    try {
      const { Items, LastEvaluatedKey } = yield this._query()
      this.data.replace(Items)
      this.LastEvaluatedKey = LastEvaluatedKey
      if (this.config.queryAll && this.LastEvaluatedKey) {
        while (this.LastEvaluatedKey) {
          yield this.queryMore()
        }
      }
    } catch (e) {
      console.error(e.message)
    }
    this.dataFetched = true
    this.isLoading = false
    return this.data
  }).bind(this)

  queryMore = flow(function * (this: DynamoDBProvider) {
    if (!this.LastEvaluatedKey) return this.data
    this.isLoading = true
    try {
      const { Items, LastEvaluatedKey } = yield this._query(this.LastEvaluatedKey)
      this.data.push(...Items)
      this.LastEvaluatedKey = LastEvaluatedKey
    } catch (e) {
      console.error(e.message)
    }
    this.isLoading = false
    return this.data
  })
}
const defaultQueryParams = {
  type: 'object',
  children: [
    { type: 'string', key: 'KeyConditionExpression' },
    { type: 'object', key: 'ExpressionAttributeValues' }
  ]
}
const DataStore = t
  .model('DataStore', {
    type: t.optional(t.string, 'DynamoDB'),
    tableName: t.maybeNull(t.string),
    indexName: t.maybeNull(t.string),
    fields: t.maybeNull(t.array(Field)),
    dataTransformer: t.late(() => t.maybeNull(DataTransformer)),
    params: t.maybeNull(t.late(() => Mapping)),
    queryAll: t.optional(t.boolean, false)
  })
  .actions(self => ({
    afterCreate () {
      if (!self.params) (self.params as IMappingSnapshot) = defaultQueryParams
    }
  }))
  .views(self => ({
    get provider () {
      return DynamoDBProvider
    },
    get mapping () {
      return self.params
    }
  }))
  .views(self => ({
    labels () {
      return {
        key: (self as any).id,
        label: (self as any).name,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: [
              {
                key: 'query',
                label: 'query',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'queryMore',
                label: 'queryMore',
                secondaryLabel: 'FUNCTION'
              }

            ]
          },
          {
            key: 'data',
            label: 'Data'
          }
        ]
      }
    }
  }))
  .actions(crudActions)

export default DataStore
